import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { fetchOnboarding, submitOnboarding, verifyAddress, formatApiError } from "@/lib/api";
import { maskSSN, validateSSN, maskDOB, validateDOB, maskPhone, validatePhone, validateAddress } from "@/lib/format";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";
const label = "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted";

const STATUS_LABELS = {
  single: "Single",
  "married-jointly": "Married filing jointly",
  "married-separately": "Married filing separately",
  "head-of-household": "Head of household",
  "qualifying-surviving-spouse": "Qualifying surviving spouse",
};

const MARRIED = ["married-jointly", "married-separately"];
const marriedSelected = (form) => MARRIED.includes(form?.filing_status);

export default function ConfirmDetails({ onDone }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [addressCheck, setAddressCheck] = useState(null);

  useEffect(() => {
    fetchOnboarding()
      .then((d) => {
        setData(d);
        setForm({
          name: d.values.name || "",
          date_of_birth: d.values.date_of_birth || "",
          ssn_itin: "",
          address: d.values.address || "",
          phone: d.values.phone || "",
          filing_status: d.values.filing_status || "single",
          dependents: d.values.dependents || 0,
          spouse_name: d.values.spouse_name || "",
          spouse_dob: d.values.spouse_dob || "",
          spouse_ssn: "",
        });
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const errors = form
    ? {
        name: form.name.trim() ? "" : "Legal name is required.",
        date_of_birth: validateDOB(form.date_of_birth),
        ssn_itin: validateSSN(form.ssn_itin, { required: !data?.values?.ssn_last4 }),
        address: validateAddress(form.address),
        phone: validatePhone(form.phone),
        spouse_dob: marriedSelected(form) ? validateDOB(form.spouse_dob, { label: "Spouse date of birth" }) : "",
        spouse_name: marriedSelected(form) && !form.spouse_name.trim() ? "Spouse legal name is required." : "",
        spouse_ssn: marriedSelected(form)
          ? validateSSN(form.spouse_ssn, { required: !data?.values?.spouse_ssn_last4 })
          : "",
      }
    : {};
  const firstError = Object.values(errors).find(Boolean) || "";

  const checkAddress = async () => {
    if (!form.address.trim()) return;
    setCheckingAddress(true);
    try {
      const res = await verifyAddress(form.address);
      setAddressCheck(res);
      if (res.formatted && res.formatted !== form.address) set("address", res.formatted);
    } catch (err) {
      setAddressCheck({ error: formatApiError(err) });
    } finally {
      setCheckingAddress(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (firstError) {
      toast.error(firstError);
      return;
    }
    setSaving(true);
    try {
      await submitOnboarding(form);
      toast.success("Thank you — your details are confirmed.");
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !form) {
    return (
      <div className="flex items-center gap-3 py-16 text-steel-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your details…
      </div>
    );
  }

  const married = marriedSelected(form);
  const hasSsn = Boolean(data.values.ssn_last4);
  const hasSpouseSsn = Boolean(data.values.spouse_ssn_last4);

  return (
    <div className="mx-auto max-w-2xl" data-testid="confirm-details">
      <div className="border border-steel-border bg-steel-surface p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
            <ShieldCheck className="h-5 w-5 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium text-steel-text">Confirm your details</h3>
            <p className="text-xs text-steel-muted">One time only — needed before your return can be prepared.</p>
          </div>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-steel-secondary">
          Please check everything below and fill in anything blank. This information goes straight onto your tax
          return, so accuracy matters. We only ever ask for the last 4 digits of your SSN — never the full number.
        </p>

        {(data.missing || []).length > 0 && (
          <div className="mt-5 flex items-start gap-3 border border-amber-500/50 bg-amber-950/20 p-4" data-testid="confirm-details-missing">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-xs leading-relaxed text-amber-300">
              Still needed: {data.missing.map((m) => m.label).join(", ")}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={label}>Legal name (as on your Social Security card)</label>
            <input data-testid="cdt-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className={label}>Date of birth</label>
            <input data-testid="cdt-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", maskDOB(e.target.value))} placeholder="MM/DD/YYYY" inputMode="numeric" />
            {errors.date_of_birth && <p className="mt-1.5 text-xs text-red-400">{errors.date_of_birth}</p>}
          </div>
          <div>
            <label className={label}>{hasSsn ? `SSN / ITIN — last 4 (on file ••• ${data.values.ssn_last4})` : "SSN / ITIN — last 4"}</label>
            <input
              data-testid="cdt-ssn"
              className={inputCls}
              value={form.ssn_itin}
              onChange={(e) => set("ssn_itin", maskSSN(e.target.value))}
              placeholder={hasSsn ? "Leave blank to keep the one on file" : "Last 4 (e.g. 6789)"}
              autoComplete="off"
              inputMode="numeric"
              maxLength={4}
            />
            {errors.ssn_itin && <p className="mt-1.5 text-xs text-red-400">{errors.ssn_itin}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Mailing address</label>
            <div className="flex gap-3">
              <input
                data-testid="cdt-address"
                className={inputCls}
                value={form.address}
                onChange={(e) => { set("address", e.target.value); setAddressCheck(null); }}
                onBlur={checkAddress}
                placeholder="12 Main St, Concord NH 03301"
              />
              <button
                type="button"
                data-testid="cdt-address-check"
                onClick={checkAddress}
                disabled={checkingAddress || !form.address.trim()}
                className="shrink-0 border border-steel-border px-4 text-xs font-medium text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
              >
                {checkingAddress ? "Checking…" : "Check"}
              </button>
            </div>
            {errors.address && <p className="mt-1.5 text-xs text-red-400">{errors.address}</p>}
            {addressCheck && !addressCheck.error && (
              <p className="mt-1.5 text-xs text-emerald-400" data-testid="cdt-address-result">
                {addressCheck.verified
                  ? "Address confirmed with USPS."
                  : addressCheck.corrected
                    ? "Tidied up to a standard format."
                    : "Format looks good."}
              </p>
            )}
          </div>
          <div>
            <label className={label}>Phone number</label>
            <input data-testid="cdt-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} placeholder="(603) 555-0100" inputMode="tel" />
            {errors.phone && <p className="mt-1.5 text-xs text-red-400">{errors.phone}</p>}
          </div>
          <div>
            <label className={label}>Filing status</label>
            <select data-testid="cdt-filing-status" className={inputCls} value={form.filing_status} onChange={(e) => set("filing_status", e.target.value)}>
              {(data.filing_statuses || []).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Number of dependents</label>
            <input data-testid="cdt-dependents" type="number" min="0" max="30" className={inputCls} value={form.dependents} onChange={(e) => set("dependents", Number(e.target.value) || 0)} />
          </div>

          {married && (
            <>
              <div className="sm:col-span-2 mt-2 border-t border-steel-border pt-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-brass">Spouse details</p>
                <p className="mt-1 text-xs text-steel-muted">Required for a married filing status.</p>
              </div>
              <div>
                <label className={label}>Spouse legal name</label>
                <input data-testid="cdt-spouse-name" className={inputCls} value={form.spouse_name} onChange={(e) => set("spouse_name", e.target.value)} />
              </div>
              <div>
                <label className={label}>Spouse date of birth</label>
                <input data-testid="cdt-spouse-dob" className={inputCls} value={form.spouse_dob} onChange={(e) => set("spouse_dob", maskDOB(e.target.value))} placeholder="MM/DD/YYYY" inputMode="numeric" />
                {errors.spouse_dob && <p className="mt-1.5 text-xs text-red-400">{errors.spouse_dob}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={label}>
                  {hasSpouseSsn ? `Spouse SSN / ITIN — last 4 (on file ••• ${data.values.spouse_ssn_last4})` : "Spouse SSN / ITIN — last 4"}
                </label>
                <input
                  data-testid="cdt-spouse-ssn"
                  className={inputCls}
                  value={form.spouse_ssn}
                  onChange={(e) => set("spouse_ssn", maskSSN(e.target.value))}
                  placeholder={hasSpouseSsn ? "Leave blank to keep the one on file" : "Last 4 (e.g. 6789)"}
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength={4}
                />
                {errors.spouse_ssn && <p className="mt-1.5 text-xs text-red-400">{errors.spouse_ssn}</p>}
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="cdt-submit"
            className="sm:col-span-2 mt-2 flex w-full items-center justify-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3.5 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {saving ? "Saving…" : "Confirm and enter my portal"}
          </button>
        </form>
      </div>
    </div>
  );
}
